import type { ServiceStatusVariant } from "kontrolEdge/helpers/serviceStatusTag";

/** The part of a detail row the summary reads; every detail mapper's row fits it. */
interface SummaryRow {
  label: string;
  value?: unknown;
  statusVariant?: ServiceStatusVariant;
  tooltip?: string;
}

interface SummarySection {
  header: string;
  items: SummaryRow[];
  table?: { data?: Array<Record<string, unknown>> };
}

export interface DetailSummaryChip {
  /** "Physical", "Service", "BGP": the row's label without "Status". */
  label: string;
  value: string;
  variant: ServiceStatusVariant;
  tooltip?: string;
}

export interface DetailSummary {
  id: string | null;
  chips: DetailSummaryChip[];
  /** Site, capacity, routing and parent, in that order, when the service has them. */
  facts: string[];
}

const ID_LABELS = new Set(["ID", "Connection ID", "DCI ID"]);
/** Rows a folded card's one-line summary skips: the page's summary or the card's editors say them. */
const NOT_SUMMARY_LABELS = new Set([...ID_LABELS, "Name", "Connection Name", "Data Center Name", "Tag"]);

/**
 * The line a card folded on a phone shows under its title (CTX-1421): its
 * first two plain rows, e.g. "Term 24 months · Auto Renew Yes". A card
 * holding only a message ("…data is unavailable") shows the message.
 */
export function sectionSummary(items: Array<SummaryRow & { type?: string }>): string {
  const rows = items.flatMap((item) => {
    // "text" is a plain row (Internet's Term); a boolean reads as the card prints it.
    const isPlain = !item.type || item.type === "text";
    const value = typeof item.value === "boolean" ? (item.value ? "Yes" : "No") : textOf(item);
    return isPlain && !item.statusVariant && value && !NOT_SUMMARY_LABELS.has(item.label)
      ? [`${item.label} ${value}`]
      : [];
  });
  if (rows.length > 0) return rows.slice(0, 2).join(" · ");
  return items.find((item) => item.type === "error")?.label ?? "";
}
const FACT_LABELS = ["Location", "Origin Location", "Capacity", "Routing", "Parent Connection"];

function textOf(row: SummaryRow | undefined): string | null {
  const value = row?.value;
  return typeof value === "string" || typeof value === "number" ? String(value) : null;
}

/**
 * What a service detail opens with on a phone (CTX-1421): its ID, the status
 * rows as chips, and one line of facts. It reads the sections the cards draw,
 * so the summary and the cards cannot disagree.
 */
export function buildDetailSummary(sections: SummarySection[]): DetailSummary {
  const rows = sections.flatMap((section) => section.items);

  const chips: DetailSummaryChip[] = rows.flatMap((row) => {
    const value = textOf(row);
    return row.statusVariant && value
      ? [{ label: row.label.replace(/ Status$/, ""), value, variant: row.statusVariant, tooltip: row.tooltip }]
      : [];
  });

  // Internet: whether its BGP sessions are up, which the table only says further down.
  const neighbors = sections.find((section) => section.header === "BGP Summary")?.table?.data ?? [];
  if (neighbors.length > 0) {
    const connected = neighbors.filter((neighbor) => neighbor.state === "Connected").length;
    chips.push({
      label: "BGP",
      value: connected === neighbors.length ? "Connected" : `${connected} of ${neighbors.length} connected`,
      variant: connected === neighbors.length ? "success" : "error",
    });
  }

  return {
    id: textOf(rows.find((row) => ID_LABELS.has(row.label))),
    chips,
    facts: FACT_LABELS.flatMap((label) => textOf(rows.find((row) => row.label === label)) ?? []),
  };
}
